import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { 
  FileText, Search, Plus, Filter, ChevronRight, Calendar,
  Clock, CheckCircle, AlertCircle, Brain, Save, Trash2,
  Edit, Eye, Download, MoreVertical, Sparkles
} from 'lucide-react';

const BehavioralHealthNotes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);

  const notes = [
    {
      id: 1,
      client: 'Sarah Johnson',
      sessionDate: '02/22/2026',
      sessionType: 'Individual Therapy',
      status: 'completed',
      createdAt: '02/22/2026 10:15 AM',
      summary: 'Discussed progress with anxiety management techniques...'
    },
    {
      id: 2,
      client: 'Michael Chen',
      sessionDate: '02/17/2026',
      sessionType: 'Couples Counseling',
      status: 'draft',
      createdAt: '02/17/2026 11:45 AM',
      summary: 'Communication patterns explored, homework assigned...'
    },
    {
      id: 3,
      client: 'Emily Rodriguez',
      sessionDate: '02/21/2026',
      sessionType: 'CBT Session',
      status: 'pending_review',
      createdAt: '02/21/2026 2:30 PM',
      summary: 'Cognitive restructuring exercises, identified negative thought patterns...'
    },
    {
      id: 4,
      client: 'David Thompson',
      sessionDate: '02/20/2026',
      sessionType: 'Initial Assessment',
      status: 'completed',
      createdAt: '02/20/2026 4:00 PM',
      summary: 'Completed intake assessment, treatment goals established...'
    },
  ];

  const templates = [
    { id: 1, name: 'Individual Therapy Note', fields: ['Presenting Issues', 'Interventions', 'Client Response', 'Plan'] },
    { id: 2, name: 'Group Therapy Note', fields: ['Group Dynamics', 'Topics Discussed', 'Individual Participation', 'Next Session'] },
    { id: 3, name: 'Initial Assessment', fields: ['Chief Complaint', 'History', 'Mental Status Exam', 'Diagnosis', 'Treatment Plan'] },
    { id: 4, name: 'Progress Note (DAP)', fields: ['Data', 'Assessment', 'Plan'] },
    { id: 5, name: 'Progress Note (SOAP)', fields: ['Subjective', 'Objective', 'Assessment', 'Plan'] },
    { id: 6, name: 'Discharge Summary', fields: ['Treatment Summary', 'Goals Met', 'Recommendations', 'Referrals'] },
  ];

  const getStatusBadge = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'draft': return 'bg-yellow-100 text-yellow-700';
      case 'pending_review': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'completed': return 'Completed';
      case 'draft': return 'Draft';
      case 'pending_review': return 'Pending Review';
      default: return status;
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FileText className="w-8 h-8 text-orange-500" />
                Session Notes
              </h1>
              <p className="text-gray-500 mt-1">Document and manage therapy session notes</p>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowTemplates(!showTemplates)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Sparkles className="w-5 h-5 text-purple-500" />
                Templates
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
                <Plus className="w-5 h-5" />
                New Note
              </button>
            </div>
          </div>
        </div>

        {/* Templates Panel */}
        {showTemplates && (
          <div className="bg-white rounded-xl shadow mb-6 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Note Templates</h3>
            <div className="grid grid-cols-3 gap-4">
              {templates.map((template) => (
                <button
                  key={template.id}
                  className="p-4 border border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors text-left"
                >
                  <h4 className="font-medium text-gray-900 mb-2">{template.name}</h4>
                  <div className="flex flex-wrap gap-1">
                    {template.fields.map((field, idx) => (
                      <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {field}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{notes.length}</h3>
                <p className="text-gray-500 text-sm">Total Notes</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center gap-4">
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Edit className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{notes.filter(n => n.status === 'draft').length}</h3>
                <p className="text-gray-500 text-sm">Drafts</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center gap-4">
              <div className="bg-orange-100 p-3 rounded-lg">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{notes.filter(n => n.status === 'pending_review').length}</h3>
                <p className="text-gray-500 text-sm">Pending Review</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{notes.filter(n => n.status === 'completed').length}</h3>
                <p className="text-gray-500 text-sm">Completed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow mb-6">
          <div className="p-4 flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search notes by client name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <select className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
              <option>All Status</option>
              <option>Completed</option>
              <option>Draft</option>
              <option>Pending Review</option>
            </select>
            <select className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
              <option>This Week</option>
              <option>This Month</option>
              <option>Last 3 Months</option>
              <option>All Time</option>
            </select>
          </div>
        </div>

        {/* Notes List */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Client</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Session Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Type</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Summary</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {notes.map((note) => (
                <tr key={note.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                        {note.client.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="font-medium text-gray-900">{note.client}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {note.sessionDate}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-700">{note.sessionType}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-500 text-sm truncate max-w-xs block">{note.summary}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(note.status)}`}>
                      {getStatusText(note.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors" title="View">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors" title="Edit">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors" title="Download">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BehavioralHealthNotes;
